import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { FaCalendarPlus, FaTimes, FaPen, FaMapMarkerAlt, FaAlignLeft, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import moment from 'moment';

// --- Custom Calendar Component ---
const CustomDatePicker = ({ selectedDate, onDateSelect }) => {
    const [displayDate, setDisplayDate] = useState(selectedDate ? new Date(selectedDate) : new Date());
    const [isOpen, setIsOpen] = useState(false);

    const daysInMonth = useMemo(() => {
        const year = displayDate.getFullYear();
        const month = displayDate.getMonth();
        const date = new Date(year, month, 1);
        const days = [];
        while (date.getMonth() === month) {
            days.push(new Date(date));
            date.setDate(date.getDate() + 1);
        }
        return days;
    }, [displayDate]);

    const firstDayOfMonth = useMemo(() => {
        return new Date(displayDate.getFullYear(), displayDate.getMonth(), 1).getDay();
    }, [displayDate]);

    const handleSelectDate = (date) => {
        onDateSelect(moment(date).format('YYYY-MM-DD'));
        setIsOpen(false);
    };

    const changeMonth = (amount) => {
        setDisplayDate(prev => new Date(prev.getFullYear(), prev.getMonth() + amount, 1));
    };

    return (
        <div className="relative">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full rounded-lg border border-gray-300 dark:border-navy-600 p-3 pl-10 text-sm font-medium text-left text-navy-700 dark:text-white outline-none focus:border-orange-500 dark:bg-navy-700 flex items-center"
            >
                <FaCalendarPlus className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                {selectedDate ? moment(selectedDate).format('MMMM D, YYYY') : <span className="text-gray-400">Select a date</span>}
            </button>

            {isOpen && (
                <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute top-full left-0 mt-2 z-10 w-full rounded-lg bg-white dark:bg-navy-800 shadow-2xl border border-gray-200 dark:border-navy-700 p-4"
                >
                    <div className="flex items-center justify-between mb-3">
                        <button type="button" onClick={() => changeMonth(-1)} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-navy-700 dark:text-white"><FaChevronLeft /></button>
                        <span className="font-bold text-navy-700 dark:text-white">{moment(displayDate).format('MMMM YYYY')}</span>
                        <button type="button" onClick={() => changeMonth(1)} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-navy-700 dark:text-white"><FaChevronRight /></button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center text-xs text-gray-500 dark:text-gray-400 mb-2">
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => <div key={day}>{day}</div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                        {Array.from({ length: firstDayOfMonth }).map((_, i) => <div key={`empty-${i}`} />)}
                        {daysInMonth.map(day => {
                            const isSelected = moment(day).isSame(selectedDate, 'day');
                            const isToday = moment(day).isSame(new Date(), 'day');
                            return (
                                <button
                                    key={day.toString()}
                                    type="button"
                                    onClick={() => handleSelectDate(day)}
                                    className={`h-8 w-8 rounded-full text-sm transition-colors text-navy-700 dark:text-white
                                        ${isSelected ? 'bg-orange-500 text-white' : ''}
                                        ${!isSelected && isToday ? 'border-2 border-orange-500' : ''}
                                        ${!isSelected ? 'hover:bg-orange-100 dark:hover:bg-navy-700' : ''}
                                    `}
                                >
                                    {day.getDate()}
                                </button>
                            );
                        })}
                    </div>
                </motion.div>
            )}
        </div>
    );
};


// --- Main Modal Component ---
const AddEventModal = ({ isOpen, onClose, onSubmit }) => {
    const [title, setTitle] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [location, setLocation] = useState('');
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setTitle('');
            setStartDate(null);
            setLocation('');
            setDescription('');
            setError('');
            setIsSubmitting(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!title.trim()) { setError('Event Title is required.'); return; }
        if (!startDate) { setError('Date is required.'); return; }
        if (!location.trim()) { setError('Location is required.'); return; }
        if (!description.trim()) { setError('Description is required.'); return; }

        setIsSubmitting(true);
        setError('');
        try {
            await onSubmit({ title, startDate, endDate: startDate, location, description });
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to create event.');
        } finally {
            setIsSubmitting(false);
        }
    };
    
    return (
        // UPDATED: Added backdrop-blur-sm for the background effect
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4" onClick={onClose}>
            <motion.div 
                initial={{ opacity: 0, scale: 0.9 }} 
                animate={{ opacity: 1, scale: 1 }} 
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                className="bg-white dark:bg-navy-800 w-full max-w-2xl p-8 relative rounded-2xl shadow-xl"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center gap-4 mb-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-500 text-white shadow-lg shadow-orange-500/30">
                        <FaPen size={20} />
                    </div>
                    <div>
                        <h4 className="text-2xl font-bold text-navy-700 dark:text-white">New Calendar Event</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Fill out the details to schedule a new event.</p>
                    </div>
                    <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors">
                        <FaTimes size={20} />
                    </button>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="relative">
                        <input type="text" placeholder="Event Title" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full rounded-lg border border-gray-300 dark:border-navy-600 p-3 pl-10 text-sm font-medium text-navy-700 dark:text-white outline-none focus:border-orange-500 dark:bg-navy-700" />
                        <FaPen className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                    <CustomDatePicker selectedDate={startDate} onDateSelect={setStartDate} />

                    <div className="relative">
                        <input type="text" placeholder="Location (e.g., Online)" value={location} onChange={(e) => setLocation(e.target.value)} className="w-full rounded-lg border border-gray-300 dark:border-navy-600 p-3 pl-10 text-sm font-medium text-navy-700 dark:text-white outline-none focus:border-orange-500 dark:bg-navy-700" />
                        <FaMapMarkerAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                    <div className="relative">
                        <textarea placeholder="Description" value={description} onChange={(e) => setDescription(e.target.value)} rows="4" className="w-full rounded-lg border border-gray-300 dark:border-navy-600 p-3 pl-10 text-sm font-medium text-navy-700 dark:text-white outline-none focus:border-orange-500 dark:bg-navy-700" />
                        <FaAlignLeft className="absolute left-3 top-4 text-gray-400" />
                    </div>

                    {error && <p className="text-center text-sm text-red-500">{error}</p>}
                    
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 p-3.5 text-base font-bold text-white shadow-lg shadow-orange-500/40 transition-all duration-300 hover:shadow-xl hover:-translate-y-px active:scale-95 disabled:opacity-60"
                    >
                        {isSubmitting ? "Saving..." : "Add to Calendar"}
                    </button>
                </form>
            </motion.div>
        </div>
    );
};

export default AddEventModal;